import React, { useCallback, useEffect, useState } from 'react'
import { CBadge, CCard, CCardBody, CSmartTable } from '@coreui/react-pro'
import moment from 'moment'

import ToastComponent from 'src/components/common/TaostComponent.js'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import UserService from 'src/service/UserService'
import './User.css'

// users/membershipMemeber only filters on these two.
const SEARCH_FIELDS = [
  { key: 'full_name', label: 'Member' },
  { key: 'email', label: 'Email' },
]

const EXPIRING_SOON_DAYS = 30

const columns = [
  { label: 'Member', key: 'full_name', filter: false },
  { label: 'Email', key: 'email', filter: false },
  { label: 'Membership', key: 'sub_type', filter: false },
  { label: 'Start Date', key: 'created_at', filter: false },
  { label: 'End Date', key: 'end_date', filter: false },
  { label: 'Status', key: 'status', filter: false, sorter: false },
]

const formatDate = (value) => (value ? moment(value).format('D.MM.YYYY') : '-')

// Memberships run for a year from the start date (end_date is computed by the API).
const membershipStatus = (endDate) => {
  if (!endDate) return { label: '-', color: 'secondary' }
  const daysLeft = moment(endDate).diff(moment(), 'days')
  if (daysLeft < 0) return { label: 'Expired', color: 'secondary' }
  if (daysLeft <= EXPIRING_SOON_DAYS) {
    return { label: `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`, color: 'warning' }
  }
  return { label: 'Active', color: 'success' }
}

const MemebershipMember = () => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('full_name')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState()

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const fetchData = useCallback(() => {
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    setLoading(true)
    UserService.membershipMemeber(offset, itemsPerPage, activePage, params)
      .then((result) => setUsers(result.data))
      .catch((e) => {
        ToastComponent(e?.response?.data?.message || 'Could not load pro members', 'error')
      })
      .finally(() => setLoading(false))
  }, [activePage, columnFilter, columnSorter, itemsPerPage])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Pro Members</h1>
        <p className="ge-page-subtitle">
          Members with a paid game or annual membership, and when it runs out.
        </p>
      </div>

      <CCard className="um-panel">
        <CCardBody>
          <div className="um-panel-heading">
            <div>
              <h2 className="um-panel-title">
                Paid Memberships
                {users?.total !== undefined && <span className="ge-tab-count">{users.total}</span>}
              </h2>
              <p className="um-panel-note">
                Memberships last one year from the start date. Mark members paid or unpaid on Manage
                Membership.
              </p>
            </div>
          </div>

          <TableSearchToolbar
            fields={SEARCH_FIELDS}
            field={searchField}
            onFieldChange={handleFieldChange}
            value={columnFilter?.[searchField] || ''}
            onSearch={applySearch}
            placeholder={`Search by ${activeSearchLabel}...`}
          />

          <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
            <CSmartTable
              columns={columns}
              columnSorter={{ external: true }}
              noItemsLabel="No paid members found"
              scopedColumns={{
                full_name: (item) => <td className="fw-semibold">{item.full_name || '-'}</td>,
                email: (item) => <td>{item.email || '-'}</td>,
                sub_type: (item) => (
                  <td>
                    <CBadge
                      color={Number(item.sub_type) === 2 ? 'primary' : 'info'}
                      shape="rounded-pill"
                      className="px-2 py-1"
                    >
                      {Number(item.sub_type) === 2 ? 'Annual' : 'Game'}
                    </CBadge>
                  </td>
                ),
                created_at: (item) => <td>{formatDate(item.created_at)}</td>,
                end_date: (item) => <td>{formatDate(item.end_date)}</td>,
                status: (item) => {
                  const status = membershipStatus(item.end_date)
                  return (
                    <td>
                      <CBadge color={status.color} shape="rounded-pill" className="px-2 py-1">
                        {status.label}
                      </CBadge>
                    </td>
                  )
                },
              }}
              items={users?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              pagination={{ external: true }}
              paginationProps={{
                activePage,
                pages: Math.ceil(users?.total / itemsPerPage) || 1,
              }}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              onActivePageChange={(page) => setActivePage(page)}
              onItemsPerPageChange={(perPage) => {
                setActivePage(1)
                setItemsPerPage(perPage)
              }}
              onSorterChange={(sorter) => setColumnSorter(sorter)}
            />
          </div>
        </CCardBody>
      </CCard>
    </>
  )
}

export default MemebershipMember
